/**
 * Task Card Component
 *
 * Displays a single task with quick actions.
 */

import { useState } from 'react';
import { format, isPast, isToday } from 'date-fns';
import type { Task } from '@/types';

interface TaskCardProps {
  task: Task;
  onComplete?: (taskId: string) => void;
  onReopen?: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
  onEdit?: (task: Task) => void;
  onClick?: (task: Task) => void;
  onFocusToggle?: (taskId: string) => void;
  compact?: boolean;
}

const priorityColors = {
  high: 'border-l-red-500 bg-red-50',
  medium: 'border-l-yellow-500 bg-yellow-50',
  low: 'border-l-gray-300 bg-gray-50',
};

const priorityLabels = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export function TaskCard({
  task,
  onComplete,
  onReopen,
  onDelete,
  onEdit,
  onClick,
  onFocusToggle,
  compact = false,
}: TaskCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const isCompleted = task.status === 'completed';
  const dueDate = task.dueAt ? new Date(task.dueAt) : null;
  const isOverdue = dueDate && isPast(dueDate) && !isCompleted;
  const isDueToday = dueDate && isToday(dueDate);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCompleted && onReopen) {
      onReopen(task.id);
    } else if (!isCompleted && onComplete) {
      onComplete(task.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(task);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(task.id);
    }
  };

  const handleFocusToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFocusToggle) {
      onFocusToggle(task.id);
    }
  };

  return (
    <div
      data-testid="task-card"
      className={`
        relative border-l-4 rounded-lg shadow-sm transition-all
        ${priorityColors[task.priority]}
        ${isCompleted ? 'opacity-60' : ''}
        ${onClick ? 'cursor-pointer hover:shadow-md' : ''}
        ${compact ? 'p-2' : 'p-4'}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick?.(task)}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={handleCheckboxClick}
          className={`
            flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center
            ${isCompleted
              ? 'bg-green-500 border-green-500 text-white'
              : 'border-gray-300 hover:border-gray-400'
            }
          `}
          aria-label={isCompleted ? 'Reopen task' : 'Complete task'}
        >
          {isCompleted && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p
              data-testid="task-title"
              className={`
                font-medium text-gray-900 flex-1
                ${isCompleted ? 'line-through text-gray-500' : ''}
                ${compact ? 'text-sm' : 'text-base'}
              `}
            >
              {task.title}
            </p>
            {/* Always-visible edit button for easy access */}
            {onEdit && (
              <button
                onClick={handleEdit}
                className="flex-shrink-0 p-0.5 text-gray-300 hover:text-primary-600 transition-colors opacity-60 hover:opacity-100"
                aria-label="Edit task"
                title="Edit task"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
          </div>

          {!compact && (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
              {/* Due date */}
              {dueDate && (
                <span
                  className={`
                    inline-flex items-center gap-1
                    ${isOverdue ? 'text-red-600 font-medium' : ''}
                    ${isDueToday && !isOverdue ? 'text-orange-600' : ''}
                  `}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {isOverdue ? 'Overdue: ' : ''}
                  {format(dueDate, 'MMM d')}
                </span>
              )}

              {/* Topic */}
              {task.topicPath && (
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                  {task.topicPath.split('/').pop()}
                </span>
              )}

              {/* Priority badge */}
              <span
                className={`
                  px-1.5 py-0.5 rounded text-xs font-medium
                  ${task.priority === 'high' ? 'bg-red-100 text-red-700' : ''}
                  ${task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : ''}
                  ${task.priority === 'low' ? 'bg-gray-100 text-gray-600' : ''}
                `}
              >
                {priorityLabels[task.priority]}
              </span>

              {/* Focus badge */}
              {task.isFocus && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="3" />
                    <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
                    <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  Focus
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions (shown on hover) */}
        {isHovered && (onComplete || onReopen || onEdit || onDelete || onFocusToggle) && (
          <div className="flex-shrink-0 flex items-center gap-1" data-testid="task-actions">
            {/* Focus toggle button */}
            {onFocusToggle && !isCompleted && (
              <button
                data-testid="focus-toggle-button"
                onClick={handleFocusToggle}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  task.isFocus
                    ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    : 'text-primary-600 bg-primary-50 hover:bg-primary-100'
                }`}
                aria-label={task.isFocus ? 'Remove focus' : 'Add to focus'}
              >
                {task.isFocus ? 'Unfocus' : 'Focus'}
              </button>
            )}
            {/* Done/Reopen button */}
            {!isCompleted && onComplete && (
              <button
                data-testid="complete-task-button"
                onClick={handleCheckboxClick}
                className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded transition-colors"
                aria-label="Mark as done"
              >
                Done
              </button>
            )}
            {isCompleted && onReopen && (
              <button
                data-testid="reopen-task-button"
                onClick={handleCheckboxClick}
                className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                aria-label="Reopen task"
              >
                Reopen
              </button>
            )}
            {onEdit && (
              <button
                data-testid="edit-task-button"
                onClick={handleEdit}
                className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                aria-label="Edit task"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                data-testid="delete-task-button"
                onClick={handleDelete}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                aria-label="Delete task"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
